import React, { Ref, ForwardedRef, useRef } from 'react';
import clsx from 'clsx';
import type { ComboBoxProps } from '@react-types/combobox';
import {
  AriaSearchFieldProps,
  useSearchField,
} from 'react-aria/useSearchField';
import styles from '@danske/sapphire-css/components/searchField/searchField.module.css';
import { useSearchFieldState } from 'react-stately/useSearchFieldState';
import { mergeProps } from 'react-aria/mergeProps';
import { useObjectRef } from 'react-aria/useObjectRef';
import { CloseOutline, Search } from '@danske/sapphire-icons/react';
import { useFocusableRef } from '@react-spectrum/utils';
import { useComboBoxState } from './useComboBoxState';
import { useComboBox } from 'react-aria/useComboBox';
import {
  ListBoxPopover,
  SapphireStyleProps,
  useButton,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { Icon } from '../../Icon';

import {
  BaseComponentKeys,
  FocusablePropsKeys,
  InputBaseKeys,
  TextInputDOMPropsKeys,
  ValueBasePropsKeys,
} from '../../utils/types';

export type SearchFieldProps<T extends object> = GlobalDomAttributes &
  SapphireStyleProps &
  Pick<
    AriaSearchFieldProps,
    | 'onSubmit'
    | 'onClear'
    | FocusablePropsKeys
    | TextInputDOMPropsKeys
    | ValueBasePropsKeys
    | InputBaseKeys
    | 'label'
    | BaseComponentKeys
  > &
  AutocompleteProps<T> & {
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
  } & {
    /**
     * @deprecated
     * Will be removed in the next major version and the autocomplete
     * functionality in the SearchField will be offered by the Autocomplete
     * component.
     */
    loadingState?: 'loading';
    /**
     * @deprecated
     * Will be removed in the next major version and the autocomplete
     * functionality in the SearchField will be offered by the Autocomplete
     * component.
     */
    loadingSkeletonRowsCount?: number;
    /**
     * This prop is badly named. It is NOT related to a selection state being
     * changed since there is no selection state in this component.
     *
     * This callback simply notifies you when the user filled in the text field
     * by selecting an option from the autocomplete dropdown. Similar to an
     * onClick.
     *
     * @deprecated
     * Will be removed in the next major version and the autocomplete
     * functionality in the SearchField will be offered by the Autocomplete
     * component.
     */
    onSelectionChange?: ComboBoxProps<T>['onSelectionChange'];
  };

// we don't want props related to "selection" because from the POV of the
// autocomplete feature, there is no concept of "selection"
/**
 * @deprecated - use Autocomplete instead
 */
interface AutocompleteProps<T extends object> {
  /** @deprecated - use Autocomplete instead */
  items?: ComboBoxProps<T>['items'];
  /** @deprecated - use Autocomplete instead */
  defaultItems?: ComboBoxProps<T>['defaultItems'];
  /** @deprecated - use Autocomplete instead */
  children?: ComboBoxProps<T>['children'];
  onOpenChange?: ComboBoxProps<T>['onOpenChange'];
  /** @deprecated - use Autocomplete instead */
  defaultFilter?: (textValue: string, inputValue: string) => boolean;
}

export type SearchFieldPropsWithRef<T extends object> = SearchFieldProps<T> & {
  // TODO(v6) remove forwardedRef
  /**
   * @deprecated Use `inputRef` instead. Will be removed in v6.
   */
  forwardedRef?: ForwardedRef<HTMLInputElement>;
  /**
   * Ref to the HTMLInput element.
   */
  inputRef?: Ref<HTMLInputElement>;
};

/**
 * Sapphire search input field.
 */
// Ref (forwardedRef) exposed as normal prop to keep TS generics working and inference of the right type for items/children passed
// Ie. type for items={items} is inferred for the item in the child render function {item => (intellisense for item available here)}
// Using forwardRef that information is lost: https://stackoverflow.com/a/58473012 and https://stackoverflow.com/a/60237948
export function SearchField<T extends object>(
  propsAndRef: SearchFieldPropsWithRef<T>
): React.JSX.Element {
  useThemeCheck();

  const {
    loadingState,
    loadingSkeletonRowsCount,
    inputRef: inputRefProp = propsAndRef.forwardedRef,
    value, // we don't want to pass this to `useComboBoxState`
    onChange, // we don't want to pass this to `useComboBoxState`
    ...props
  } = propsAndRef;

  const inputRef = useObjectRef(inputRefProp);
  const buttonRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useFocusableRef<HTMLUListElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useFocusableRef<HTMLDivElement>(null);
  const {
    styleProps: { style, className },
  } = useSapphireStyleProps(props);

  const state = useComboBoxState({
    ...props,
    inputValue: value,
    defaultInputValue: props.defaultValue,
    onInputChange: onChange,
    allowsEmptyCollection: false,
    allowsCustomValue: true,
    shouldCloseOnBlur: true,
    onSelectionChange: (key) => {
      if (key !== null && props.onSelectionChange) {
        props.onSelectionChange(key);
      }
    },
  });

  const isOpen = state.isOpen && state.collection.size > 0;

  const { inputProps: comboBoxInputProps, listBoxProps } = useComboBox(
    {
      ...props,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    // Prevent ariaHideOutside setting aria-hidden on the clear button
    // https://github.com/adobe/react-spectrum/blob/bae71790493686f027e49ad67a68845426ae8543/packages/%40react-aria/combobox/src/useComboBox.ts#L344
    { ...state, isOpen }
  );

  /**
   * In order to compose the `useSearchField` and `useComboBox` we want to:
   *  - let the combobox control the input but
   *  - allow the search field's "clear" and "submit" behavior
   *
   *  For this, we take control of `useSearchField` and not pass the props
   *  indiscriminately.
   *
   *  For example think about using `onBlur` on `SearchField`. The combobox
   *  hook gets that prop and the input props we get back will add that
   *  listener to the input. We don't want the input props from
   *  `useSearchField` to add the same listener.
   */
  const searchProps: AriaSearchFieldProps = {
    'aria-label': props['aria-label'],
    'aria-labelledby': props['aria-labelledby'],
    isDisabled: props.isDisabled,
    isReadOnly: props.isReadOnly,
    // needed for the submit functionality
    value: state.inputValue,
    onSubmit: (value) => {
      if (!state.selectionManager.isFocused) {
        props?.onSubmit?.(value);
      }
    },
    onClear: () => {
      state.setInputValue('');
      props.onClear?.();
    },
  };

  const searchFieldState = useSearchFieldState(searchProps);

  const { clearButtonProps, inputProps: searchFieldInputProps } =
    useSearchField(searchProps, searchFieldState, inputRef);

  const { buttonProps } = useButton(
    {
      ...clearButtonProps,
      elementType: 'div',
    },
    buttonRef
  );

  return (
    <div
      className={clsx(styles['sapphire-search-field'], className, {
        [styles['sapphire-search-field--md']]: props.size === 'md',
        [styles['is-disabled']]: props.isDisabled,
      })}
      style={style}
      ref={triggerRef}
    >
      <span className={styles['sapphire-search-field__icon']}>
        <Icon size={props.size === 'md' ? 'sm' : 'md'}>
          <Search />
        </Icon>
      </span>
      <input
        {...mergeProps(comboBoxInputProps, searchFieldInputProps)}
        ref={inputRef}
        className={clsx(styles['sapphire-search-field__input'], {
          [styles['sapphire-search-field__input--empty']]:
            !state.inputValue || state.inputValue.length === 0,
        })}
      />
      <div
        ref={buttonRef}
        className={styles['sapphire-search-field__button']}
        {...buttonProps}
      >
        <Icon size={props.size === 'md' ? 'sm' : 'md'}>
          <CloseOutline />
        </Icon>
      </div>
      {state.isOpen && (
        <ListBoxPopover<T>
          state={state}
          triggerRef={triggerRef}
          popoverRef={popoverRef}
          listBoxRef={listBoxRef}
          listBoxProps={listBoxProps}
          loadingState={loadingState}
          loadingSkeletonRowsCount={loadingSkeletonRowsCount}
          selectWidth={triggerRef.current?.offsetWidth}
          size={props.size}
          disableSelectedStyles
          isNonModal
        >
          {props.children || []}
        </ListBoxPopover>
      )}
    </div>
  );
}
